import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material';
import StyledDataGrid from '../../../components/StyledDataGrid';
import { createUsageRecord, fetchProjects, fetchUsageRecords } from '../../../utils/api';
import { chicagoDateTimeLocalToIso, formatDateTimeInAppTimeZone, isoToChicagoDateTimeLocal } from '../../../utils/appDateTime';

const UsageHistory = () => {
  const [records, setRecords] = useState([]);
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState({ page: 0, pageSize: 25 });
  const [filters, setFilters] = useState({ aliquotId: '', projectId: '' });
  const [applied, setApplied] = useState({});
  const [refresh, setRefresh] = useState(0);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ aliquotId: '', projectId: '', usedAt: isoToChicagoDateTimeLocal() });

  useEffect(() => {
    fetchProjects().then(data => setProjects(Array.isArray(data) ? data : data?.content || []))
      .catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchUsageRecords({ ...applied, page: page.page, size: page.pageSize })
      .then(result => {
        if (!active) return;
        setRecords(result?.content || []);
        setTotal(result?.totalElements || 0);
        setError('');
      })
      .catch(err => {
        if (!active) return;
        setRecords([]);
        setTotal(0);
        setError(err?.response?.status === 403 ? 'Admin access is required for usage history.' : 'Could not load usage history.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [applied, page, refresh]);

  // TODO: Support linking a batch of aliquots to one project; parsed emails may supply the batch later.
  const save = async () => {
    if (!form.aliquotId.trim() || !form.projectId.trim() || !form.usedAt) {
      setError('Aliquot, project, and actual use time are required.');
      return;
    }
    setSaving(true);
    try {
      await createUsageRecord({
        aliquotId: form.aliquotId.trim(),
        projectId: form.projectId.trim(),
        usedAt: chicagoDateTimeLocalToIso(form.usedAt),
      });
      setOpen(false);
      setError('');
      setRefresh(value => value + 1);
    } catch (err) {
      setError(err?.data?.status === 'unconfirmed' && err.data.recordType === 'usage'
        ? `Usage write unconfirmed. ID: ${err.data.recordId}. Reconcile before retrying.`
        : err?.response?.status === 403 ? 'Admin access is required to record usage.'
          : 'Usage was not recorded. Check the shipment, project, and Study attribution.');
    } finally {
      setSaving(false);
    }
  };

  const projectName = (id) => projects.find(project => project.projectId === id)?.projectName || id;
  const columns = [
    { field: 'aliquotId', headerName: 'Aliquot ID', flex: 1, minWidth: 170 },
    { field: 'projectId', headerName: 'Project', flex: 1, minWidth: 170,
      renderCell: params => projectName(params.value) },
    { field: 'usedAt', headerName: 'Actual Use Time', flex: 1, minWidth: 190,
      renderCell: params => params.value ? formatDateTimeInAppTimeZone(params.value) : 'N/A' },
    { field: 'usageId', headerName: 'Usage ID', flex: 1, minWidth: 220 },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 500 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Aliquot ID" value={filters.aliquotId}
          onChange={event => setFilters(value => ({ ...value, aliquotId: event.target.value }))} />
        <TextField select size="small" placeholder="Project" value={filters.projectId} sx={{ minWidth: 220 }}
          SelectProps={{
            displayEmpty: true,
            renderValue: selected => selected
              ? projectName(selected)
              : <span style={{ color: '#757575' }}>Project</span>,
          }}
          inputProps={{ 'aria-label': 'Project' }}
          onChange={event => setFilters(value => ({ ...value, projectId: event.target.value }))}>
          <MenuItem value="">All projects</MenuItem>
          {projects.map(project => <MenuItem key={project.projectId} value={project.projectId}>{project.projectName}</MenuItem>)}
        </TextField>
        <Button variant="outlined" onClick={() => { setFilters({ aliquotId: '', projectId: '' }); setApplied({}); setPage(value => ({ ...value, page: 0 })); }}>Clear</Button>
        <Button variant="contained" onClick={() => { setApplied(Object.fromEntries(Object.entries(filters).filter(([, value]) => value.trim()))); setPage(value => ({ ...value, page: 0 })); }}>Search</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" onClick={() => setOpen(true)}>Record Actual Use</Button>
      </Box>
      {error && <Typography color="error">{error}</Typography>}
      <Box sx={{ flex: 1, minHeight: 400 }}>
        <StyledDataGrid rows={records.map(record => ({ ...record, id: record.usageId }))} columns={columns}
          loading={loading} pagination paginationMode="server" rowCount={total}
          paginationModel={page} onPaginationModelChange={setPage} pageSizeOptions={[10, 25, 50]} />
      </Box>
      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Actual Use</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '20px !important' }}>
          <TextField label="Aliquot ID" required value={form.aliquotId}
            onChange={event => setForm(value => ({ ...value, aliquotId: event.target.value }))} />
          <TextField select label="Project" required value={form.projectId}
            onChange={event => setForm(value => ({ ...value, projectId: event.target.value }))}>
            {projects.map(project => <MenuItem key={project.projectId} value={project.projectId}>{project.projectName}</MenuItem>)}
          </TextField>
          <TextField type="datetime-local" label="Actual Use Time" required InputLabelProps={{ shrink: true }}
            value={form.usedAt} onChange={event => setForm(value => ({ ...value, usedAt: event.target.value }))} />
          <Typography variant="body2">Bulk linking multiple aliquots to one project is planned.</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button><Button onClick={save} disabled={saving} variant="contained">Save</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsageHistory;
